import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  CartItem,
  CartRepository,
  Loading,
  Product,
  ProductsRepository,
} from "../domain";
import { ServiceLocater, Services } from "../service-locater";
import { AppThunk } from "./store";

type CartState = {
  items: CartItem[];
  itemsLoading: Loading;
};

const initialState: CartState = {
  items: [],
  itemsLoading: "idle",
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    cartItemsLoading(state) {
      state.itemsLoading = "pending";
    },
    cartItemsLoaded(state, { payload }: PayloadAction<CartItem[]>) {
      state.items = payload;
      state.itemsLoading = "succeeded";
    },
    cartItemsFailed(state) {
      state.itemsLoading = "failed";
    },
    addToCart(
      state,
      {
        payload: { productId, attributeId, product, count },
      }: PayloadAction<{
        productId: string;
        attributeId: string;
        count: number;
        product: Product;
      }>
    ) {
      const itemIndex = state.items.findIndex(
        (item) =>
          item.productId === productId && item.attributeId === attributeId
      );
      if (itemIndex === -1) {
        ServiceLocater.get<CartRepository>(Services.CartRepository).addToCart(
          productId,
          attributeId,
          count
        );
        state.items.push(
          new CartItem()
            .deserialize({ productId, attributeId, count })
            .setProduct(product)
        );
      } else {
        const attibute = state.items[itemIndex].product.attributes.find(
          (attribute) => attribute.id === attributeId
        );
        if (attibute!.count > state.items[itemIndex].count) {
          ServiceLocater.get<CartRepository>(Services.CartRepository).addToCart(
            productId,
            attributeId,
            count
          );
          state.items[itemIndex] = Object.assign({}, state.items[itemIndex], {
            count: state.items[itemIndex].count + 1,
          });
        }
      }
    },
    removeFromCart(
      state,
      {
        payload: { productId, attributeId },
      }: PayloadAction<{ productId: string; attributeId: string }>
    ) {
      ServiceLocater.get<CartRepository>(
        Services.CartRepository
      ).removeFromCart(productId, attributeId);
      state.items = state.items.filter(
        (item: CartItem) =>
          item.productId !== productId && item.attributeId !== attributeId
      );
    },
    increaseCartItemCount(
      state,
      {
        payload: { productId, attributeId },
      }: PayloadAction<{ productId: string; attributeId: string }>
    ) {
      ServiceLocater.get<CartRepository>(Services.CartRepository).increaseCount(
        productId,
        attributeId
      );
      const itemIndex = state.items.findIndex(
        (item: CartItem) =>
          item.productId === productId && item.attributeId === attributeId
      );
      state.items[itemIndex] = Object.assign({}, state.items[itemIndex], {
        count: state.items[itemIndex].count + 1,
      });
    },
    decreaseCartItemCount(
      state,
      {
        payload: { productId, attributeId },
      }: PayloadAction<{ productId: string; attributeId: string }>
    ) {
      const itemIndex = state.items.findIndex(
        (item: CartItem) =>
          item.productId === productId && item.attributeId === attributeId
      );
      if (state.items[itemIndex].count > 1) {
        ServiceLocater.get<CartRepository>(
          Services.CartRepository
        ).decreseCount(productId, attributeId);
        state.items[itemIndex] = Object.assign({}, state.items[itemIndex], {
          count: state.items[itemIndex].count - 1,
        });
      }
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  increaseCartItemCount,
  decreaseCartItemCount,
} = cartSlice.actions;
export default cartSlice.reducer;

const {
  cartItemsFailed,
  cartItemsLoaded,
  cartItemsLoading,
} = cartSlice.actions;

export const loadCartItems = (): AppThunk => async (dispatch) => {
  dispatch(cartItemsLoading());
  const cartItems = ServiceLocater.get<CartRepository>(
    Services.CartRepository
  ).loadCartItems();
  const cartProducts = await ServiceLocater.get<ProductsRepository>(
    Services.ProductsRepository
  ).getCartProducts(cartItems.map((item) => item.productId));
  cartProducts.fold(
    (_) => {
      dispatch(cartItemsFailed());
    },
    (products) => {
      // remove products which are no longer available
      for (let index = 0; index < cartItems.length; index++) {
        const productIndex = products.findIndex(
          (product) => product.id.toString() === cartItems[index].productId
        );
        if (productIndex === -1) {
          ServiceLocater.get<CartRepository>(
            Services.CartRepository
          ).removeFromCart(
            cartItems[index].productId,
            cartItems[index].attributeId
          );
        } else {
          const attributeIndex = products[productIndex].attributes.findIndex(
            (attribute) =>
              attribute.id.toString() === cartItems[index].attributeId
          );
          if (attributeIndex === -1) {
            ServiceLocater.get<CartRepository>(
              Services.CartRepository
            ).removeFromCart(
              cartItems[index].productId,
              cartItems[index].attributeId
            );
          }
        }
      }

      const finalCartItems = ServiceLocater.get<CartRepository>(
        Services.CartRepository
      ).loadCartItems();

      for (let index = 0; index < finalCartItems.length; index++) {
        const productIndex = products.findIndex(
          (product) => product.id.toString() === finalCartItems[index].productId
        );
        if (productIndex !== -1) {
          finalCartItems[index].setProduct(products[productIndex]);
        }
      }
      dispatch(cartItemsLoaded(finalCartItems));
    }
  );
};
